// Extracted from original/src/App.tsx:54572-55592 (the dashboard quick-operation modal `{showQuickTx && (…)}`:
// "إيداع في الخزنة" / "سحب من الخزنة", ADR-08 §2.1). Behaviour specification: D04_BOXES_AND_VAULT.md §3 and §5
// "Quick deposit / withdraw" — box mode: deposit = the customer pays `amount` cash into the vault and the office
// sends `cost` (≤ amount, default = amount) from the digital box, cash commission = amount − cost; withdraw is the
// mirror; the optional digital commission is typed or auto-calculated. NOTHING of that is computed here: every
// figure (cash commission, balances, the "insufficient balance" alert, the auto-fill hint) arrives preformatted
// in `summary`, and the "احتساب عمولة رقمية" button only calls `onAutoCommission()` (the container asks the server).
// Kept: backdrop + dialog (:54573-54592), the red/green header (:54594-54623), the counterparty toggle
// (:54631-54687), the customer block (:54689-54801: name input + dropdown, allocation ديون / أمانات), the box
// dropdown with search (:54803-54869; the old `type !== "vault"` / id 1, 11, 12 / name filters at :54842 are gone —
// only digital boxes arrive in `boxOptions`, D-077), amount + cost inputs with the balance caption and the
// auto-fill hint (:54871-54953), the insufficient-balance alert (:54955-55004 box mode, :55084-55132 customer
// mode — same markup, one `summary.insufficient` prop), the partial-payment block (:55006-55082), the commission
// column (:55137-55212), the notes textarea (:55214-55222), the due-date alert block (:55224-55573) and the
// footer button (:55580-55588).
// Cut: `handleQuickTx`, `resetQuickTx`, the vault / sub-safe balance lookups (:54875-54910, :54964-54972),
// `parseNumeric` / `formatNumber` arithmetic (:54948, :55155-55158), the commission-rate lookup and calculation
// (:55179-55204), the partial-payment redistribution (:54917-54926, :55033-55076), the customer search over `debts`
// (:54706-54713, :54719-54759 — `customerOptions` arrive filtered for the current allocation; the text filter with
// the Arabic letter normalisation of :54720 is kept as a pure UI filter), the counterparty-switch side effects
// (:54635-54677, container), and the `new Date()` shortcuts of the due-date block (→ `onDueDateShortcut`,
// `onDueTimeNow`). Mode: `box` is the only mode the C19 container enables; the customer counterparty ("حساب عميل",
// D-097) keeps its markup here behind `mode === 'customer'` and its data props, and is wired with D03 in a later chat.
// Added: `data-testid`s. Dropdown open flags stay inside the component (pure UI state).
import { useState } from 'react';
import { motion } from 'motion/react';
import { AlertTriangle, ArrowUpRight, Bell, Calculator, Calendar, Clock, Info, ShieldCheck, X } from 'lucide-react';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { NumericInput } from '../components/NumericInput.js';
import { useT } from '../i18n-context.js';

export type QuickTxType = 'deposit' | 'withdraw';
export type QuickTxMode = 'box' | 'customer';
/** Old values 'دين' / 'أمانة' (:54665, :54771, :54786). */
export type QuickTxAllocation = 'debt' | 'custody';

export interface QuickTxBoxOption {
  readonly id: string;
  readonly label: string;
  /** Formatted by the container, e.g. "1,250,000 د.ع" (old: `${formatNumber(w.balance)} د.ع`, :54860). */
  readonly balanceLabel: string;
}

export interface QuickTxCustomerOption {
  readonly id: string;
  readonly label: string;
  /** Formatted by the container, e.g. "(250,000 د.ع)" (old :54755). */
  readonly balanceLabel: string;
}

export interface QuickTxFormValues {
  readonly boxId: string;
  readonly amount: string;
  readonly cost: string;
  readonly digitalCommission: string;
  readonly note: string;
  // ---- customer mode (D-097; wired with D03 in a later chat) ----
  readonly customerId: string;
  readonly customerName: string;
  readonly allocation: QuickTxAllocation;
  readonly partialBreakdown: boolean;
  readonly cashReceived: string;
  readonly remainingOnAccount: string;
  readonly deferred: boolean;
  /** YYYY-MM-DD or "". */
  readonly dueDate: string;
  readonly dueHour: string;
  readonly dueMinute: string;
  readonly dueSecond: string;
}

/** Every figure is a string formatted by the container (ADR-02 §2.9: no money arithmetic in the UI). */
export interface QuickTxSummary {
  /** The caption above the amount (:54875-54910): the vault (withdraw) or the selected box (deposit) balance. */
  readonly balanceSource?: 'vault' | 'box';
  readonly balanceLabel?: string;
  /** The amount reformatted for the "انقر لملء الكلفة التلقائية" hint (:54948); shown while `values.amount` is non-empty. */
  readonly amountLabel?: string;
  /** Cash commission = amount − cost (:55155-55158), "0" when cost is empty. */
  readonly cashCommissionLabel: string;
  /** Digital commission rate for the button caption, e.g. "0.1" (:55206). */
  readonly digitalCommissionRateLabel: string;
  /** Present only when the container found the balance insufficient (:54974, :54990, :55104, :55119). */
  readonly insufficient?: {
    readonly costLabel: string;
    readonly availableLabel: string;
    /** 'vault' | 'sub_safe' (withdraw) or 'box' (deposit; the selected box label is used). */
    readonly place: 'vault' | 'sub_safe' | 'box';
  };
  /** Customer mode: the balance of the typed customer in the current allocation, formatted "(250,000)" (:54710). */
  readonly customerBalanceLabel?: string;
  /** Customer mode: the scheduled alert badge text, e.g. "2026-10-22 09:00:00" (:55258). */
  readonly dueAtLabel?: string;
}

export interface QuickTxModalProps {
  readonly open: boolean;
  readonly type: QuickTxType;
  readonly mode: QuickTxMode;
  readonly onModeChange: (mode: QuickTxMode) => void;
  readonly values: QuickTxFormValues;
  readonly onChange: (patch: Partial<QuickTxFormValues>) => void;
  /** Digital boxes only (D-077). */
  readonly boxOptions: readonly QuickTxBoxOption[];
  /** Customer mode: names of the current allocation's register, filtered by the container (D03, later chat). */
  readonly customerOptions?: readonly QuickTxCustomerOption[];
  readonly summary: QuickTxSummary;
  /** "احتساب عمولة رقمية (…%)": the container asks the server and writes `values.digitalCommission`. */
  readonly onAutoCommission: () => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  /** Customer mode: due-date shortcuts (today = 0, +1, +3, +7, +30 days; the container owns the clock). */
  readonly onDueDateShortcut?: (daysFromToday: number) => void;
  /** Customer mode: "الوقت الحالي الآن" (:55348-55356). */
  readonly onDueTimeNow?: () => void;
  readonly busy?: boolean;
  readonly error?: string | null;
}

const TIME_BOX_INPUT = "w-full text-center font-mono font-black text-xs text-gray-800 bg-transparent py-1 focus:outline-none";
const TIME_BOX_BTN = "w-5 h-7 flex items-center justify-center bg-purple-100 hover:bg-purple-200 text-purple-800 font-bold text-xs cursor-pointer";
const DUE_SHORTCUT_BTN = "px-2 py-0.5 text-[10px] font-bold bg-white hover:bg-purple-100 text-purple-800 rounded-md border border-purple-200 transition-colors shadow-2xs cursor-pointer";
const ALERT_BOX = "mt-4 bg-[#FFF5F5] border border-[#FFE3E3] rounded-xl p-4 flex items-start gap-3 text-right";

/** Old :54720 — Arabic letter normalisation of the customer search (pure text filter, no data lookup). */
const normalizeArabic = (s: string) => s.replace(/[أإآا]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي').toLowerCase();

const pad2 = (s: string) => String(s || "00").padStart(2, "0");

/** One HH / MM / SS box of :55365-55514 (identical markup three times). Clock wrap only; not money. */
function TimeBox(props: {
  readonly label: string;
  readonly value: string;
  readonly max: number;
  readonly placeholder: string;
  readonly testId: string;
  readonly onValue: (v: string) => void;
}) {
  const step = (delta: number) => {
    const n = (parseInt(props.value || "0") + delta + (props.max + 1)) % (props.max + 1);
    props.onValue(String(n).padStart(2, "0"));
  };
  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <span className="text-[9px] font-bold text-purple-700 mb-0.5">
        {props.label}
      </span>
      <div className="flex items-center w-full border border-purple-200 rounded-lg overflow-hidden bg-purple-50/40">
        <button type="button" onClick={() => step(-1)} className={TIME_BOX_BTN}>
          -
        </button>
        <input
          type="number"
          min="0"
          max={String(props.max)}
          data-testid={props.testId}
          value={props.value || ''}
          onChange={(e) => {
            let val = e.target.value;
            if (val !== "") {
              const num = Math.min(props.max, Math.max(0, parseInt(val) || 0));
              val = String(num).padStart(2, "0");
            }
            props.onValue(val);
          }}
          onBlur={() => props.onValue(String(parseInt(props.value || "0") || 0).padStart(2, "0"))}
          className={TIME_BOX_INPUT}
          placeholder={props.placeholder}
        />
        <button type="button" onClick={() => step(1)} className={TIME_BOX_BTN}>
          +
        </button>
      </div>
    </div>
  );
}

export function QuickTxModal(props: QuickTxModalProps) {
  const t = useT();
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  const [walletSearch, setWalletSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  if (!props.open) return null;

  const v = props.values;
  const type = props.type;
  const mode = props.mode;
  const isCustomer = mode === 'customer';
  const selectedBox = props.boxOptions.find((o) => o.id === v.boxId);
  const registerName = v.allocation === 'debt' ? t('quick.register_debts') : t('quick.register_custody');

  const close = () => props.onClose();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!props.busy) props.onSubmit();
  };

  /** The old time handlers also set the due date to today when empty (:55376 etc.). */
  const setTime = (patch: Partial<QuickTxFormValues>) => {
    props.onChange(patch);
    if (!v.dueDate) props.onDueDateShortcut?.(0);
  };

  const insufficientAlert = props.summary.insufficient ? (
    <div className={ALERT_BOX} dir="rtl" data-testid="quick-tx-insufficient">
      <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
      <div className="text-sm text-red-700 font-bold leading-relaxed">
        {t(type === 'withdraw' ? 'quick.insufficient_withdraw' : 'quick.insufficient_deposit', {
          cost: props.summary.insufficient.costLabel,
          available: props.summary.insufficient.availableLabel,
          place:
            props.summary.insufficient.place === 'sub_safe'
              ? t('quick.place_sub_safe')
              : props.summary.insufficient.place === 'vault'
                ? t('quick.place_vault')
                : (selectedBox?.label ?? ''),
        })}
      </div>
    </div>
  ) : null;

  const customerMatches = (props.customerOptions ?? []).filter((o) => {
    const normSearch = normalizeArabic(v.customerName.trim());
    return !normSearch || normalizeArabic(o.label).includes(normSearch);
  });

  return (
    <div key="quick-tx-modal-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4" data-testid="quick-tx-modal">
      <motion.div
        key="quick-tx-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={close}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <motion.div
        key="quick-tx-modal-dialog"
        initial={{ scale: 0.96, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 10 }}
        className="bg-white w-full max-w-4xl rounded-[28px] shadow-2xl relative z-10 overflow-hidden flex flex-col max-h-[calc(100vh-1.5rem)]"
        dir="rtl"
      >
        {/* Modal Header */}
        <div
          className={`px-6 py-4 flex justify-between items-center shrink-0 ${
            type === "deposit"
              ? "bg-[#FFF1F2]"
              : "bg-[#F0FDF4]"
          }`}
        >
          <button
            type="button"
            onClick={close}
            data-testid="quick-tx-close"
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-all"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 font-extrabold text-lg sm:text-xl text-gray-900">
            <span>
              {type === "withdraw" ? t('withdraw') : t('deposit')}
            </span>
            <span
              className={`text-xl font-black ${
                type === "withdraw" ? "text-[#10B981]" : "text-[#F43F5E]"
              }`}
            >
              {type === "withdraw" ? "↙" : "↗"}
            </span>
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
            <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] lg:grid-cols-[1.3fr_1fr] gap-6 items-start">

              {/* RIGHT COLUMN: Inputs (First child in RTL) */}
              <div className="space-y-4">
                <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                    <button
                      type="button"
                      data-testid="quick-tx-mode-box"
                      onClick={() => props.onModeChange('box')}
                      className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${
                        mode === "box"
                          ? "bg-white border border-gray-300 text-[#1E3A8A] shadow-sm"
                          : "text-gray-500"
                      }`}
                    >
                      {t('quick.mode_box')}
                    </button>
                    <button
                      type="button"
                      data-testid="quick-tx-mode-customer"
                      onClick={() => props.onModeChange('customer')}
                      className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${
                        mode === "customer"
                          ? "bg-white border border-gray-300 text-[#1E3A8A] shadow-sm"
                          : "text-gray-500"
                      }`}
                    >
                      {t('quick.mode_customer')}
                    </button>
                  </div>

                {isCustomer ? (
                  <>
                    <div className="text-right relative">
                      <label className="block text-xs text-gray-400 font-bold mb-1.5">{t('customer')}</label>
                      <div className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                        <input
                          type="text"
                          required
                          data-testid="quick-tx-customer-name"
                          className="flex-1 bg-transparent text-right font-bold text-gray-800 focus:outline-none"
                          placeholder={t('customer')}
                          value={v.customerName || ''}
                          onChange={(e) => {
                            props.onChange({ customerName: e.target.value, customerId: '' });
                            setShowCustomerDropdown(true);
                          }}
                          onFocus={() => setShowCustomerDropdown(true)}
                        />
                        {props.summary.customerBalanceLabel ? (
                          <span className="text-gray-400 font-mono text-sm font-bold ml-2 whitespace-nowrap" dir="ltr">
                            {props.summary.customerBalanceLabel}
                          </span>
                        ) : null}
                      </div>
                      {showCustomerDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowCustomerDropdown(false)} />
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto p-1 space-y-0.5" data-testid="quick-tx-customer-dropdown">
                            {customerMatches.length === 0 ? (
                              <div className="p-3 text-center text-xs text-gray-400 font-bold">
                                {v.customerName.trim()
                                  ? t('quick.no_matches_in', { register: registerName })
                                  : t('quick.no_names_in', { register: registerName })}
                              </div>
                            ) : (
                              customerMatches.map((o, i) => (
                                <button
                                  key={`quick-tx-cust-${o.id}-${i}`}
                                  type="button"
                                  data-testid={`quick-tx-customer-${o.id}`}
                                  onClick={() => {
                                    props.onChange({ customerId: o.id, customerName: o.label });
                                    setShowCustomerDropdown(false);
                                  }}
                                  className="w-full text-right px-3 py-2 hover:bg-blue-50/70 rounded-lg text-sm font-bold text-gray-700 flex justify-between items-center transition-colors"
                                >
                                  <span>{o.label}</span>
                                  <span className="text-gray-500 font-mono text-xs" dir="ltr">
                                    {o.balanceLabel}
                                  </span>
                                </button>
                              ))
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="text-right">
                      <label className="block text-xs text-gray-400 font-bold mb-1.5">{t('quick.customer_op_type')}</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          data-testid="quick-tx-allocation-debt"
                          onClick={() => {
                            props.onChange({ allocation: 'debt', customerName: '', customerId: '' });
                            setShowCustomerDropdown(true);
                          }}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                            v.allocation === 'debt'
                              ? 'bg-rose-50 border border-rose-300 text-rose-600'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <ArrowUpRight className={`w-4 h-4 ${v.allocation === 'debt' ? 'text-rose-600' : 'text-rose-500'}`} /> {t('quick.allocation_debts')}
                        </button>
                        <button
                          type="button"
                          data-testid="quick-tx-allocation-custody"
                          onClick={() => {
                            props.onChange({ allocation: 'custody', customerName: '', customerId: '' });
                            setShowCustomerDropdown(true);
                          }}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                            v.allocation === 'custody'
                              ? 'bg-emerald-50 border border-emerald-300 text-emerald-600'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <ShieldCheck className={`w-4 h-4 ${v.allocation === 'custody' ? 'text-emerald-600' : 'text-emerald-500'}`} /> {t('quick.allocation_custody')}
                        </button>
                      </div>
                    </div>
                  </>
                ) : null}

                <div className="text-right">
                  <label className="block text-xs text-gray-400 font-bold mb-1.5">
                    {isCustomer ? t('quick.select_affected_box') : (type === "deposit" ? t('quick.select_box_source') : t('quick.select_box_receiver'))}
                  </label>
                  <div className="relative">
                    <div
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-right font-bold text-gray-800 flex justify-between items-center cursor-pointer focus:outline-none focus:border-blue-500"
                      data-testid="quick-tx-box-select"
                      onClick={() => setShowWalletDropdown(!showWalletDropdown)}
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      <span>
                        {selectedBox
                          ? `${selectedBox.label}`
                          : (type === "deposit" && mode === "box") ? t('quick.select_source') : t('selectBox')}
                      </span>
                    </div>
                    {showWalletDropdown && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowWalletDropdown(false)} />
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 flex flex-col" style={{ maxHeight: '300px' }} data-testid="quick-tx-box-dropdown">
                          <div className="p-3 border-b border-gray-100 bg-gray-50/80 text-right">
                            <input
                              type="text"
                              placeholder={t('quick.select_box_search')}
                              className="w-full bg-transparent text-right font-bold text-gray-800 text-sm focus:outline-none placeholder-gray-400"
                              value={walletSearch || ''}
                              onChange={(e) => setWalletSearch(e.target.value)}
                              autoFocus
                            />
                          </div>
                          <div className="overflow-y-auto flex-1 custom-scrollbar">
                            <div className="px-4 py-2 bg-white text-gray-400 font-bold border-b border-slate-100 text-xs text-right">
                              {t('quick.boxes_list_heading')}
                            </div>
                            <div className="divide-y divide-gray-50">
                              {props.boxOptions
                                .filter((o) => walletSearch ? o.label.toLowerCase().includes(walletSearch.toLowerCase()) : true)
                                .map((o) => (
                                  <button
                                    key={`quick-tx-wallet-${o.id}`}
                                    type="button"
                                    data-testid={`quick-tx-box-${o.id}`}
                                    className="w-full text-right px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex justify-between items-center transition-colors"
                                    onClick={() => {
                                      props.onChange({ boxId: o.id });
                                      setShowWalletDropdown(false);
                                      setWalletSearch("");
                                    }}
                                  >
                                    <span>{o.label}</span>
                                    <span className="text-gray-500 font-mono text-xs" dir="ltr">{o.balanceLabel}</span>
                                  </button>
                                ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <div className="flex-1 text-right">
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="block text-xs text-gray-400 font-bold">{t('amount')}</label>
                      {props.summary.balanceSource && props.summary.balanceLabel !== undefined ? (
                        <span className="text-[11px] font-bold text-slate-500" data-testid="quick-tx-balance-caption">
                          {props.summary.balanceSource === 'vault'
                            ? t('quick.vault_balance')
                            : t('quick.box_balance', { name: selectedBox?.label ?? '' })} <span className="text-blue-600 font-mono">{props.summary.balanceLabel}</span>
                        </span>
                      ) : null}
                    </div>
                    <NumericInput
                       required
                       value={v.amount ?? ''}
                       onChange={(val) => props.onChange({ amount: val })}
                       className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-right font-bold text-[#64748B] text-lg focus:outline-none focus:border-blue-500 font-mono"
                       placeholder="0.00"
                    />
                  </div>
                  <div className="flex-1 text-right">
                    <label className="block text-xs text-gray-400 font-bold mb-1.5">{t('cost')}</label>
                    <NumericInput
                      value={v.cost ?? ''}
                      onChange={(val) => props.onChange({ cost: val })}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-right font-bold text-[#64748B] text-lg focus:outline-none focus:border-blue-500 font-mono"
                      placeholder="0.00"
                    />
                    {v.amount && (
                       <div className="mt-2 text-right">
                         <button
                           type="button"
                           data-testid="quick-tx-cost-autofill"
                           onClick={() => props.onChange({ cost: v.amount })}
                           className="text-[11px] font-bold text-gray-500 hover:text-gray-800 transition-colors flex items-center justify-end gap-1 w-full"
                         >
                           <span>{t('quick.autofill_cost_hint')}</span>
                           <span className="text-emerald-500 font-mono">{props.summary.amountLabel ?? ''}</span>
                         </button>
                       </div>
                    )}
                  </div>
                </div>

                {!isCustomer ? insufficientAlert : null}

                {isCustomer && (
                  <>
                    <div className="mt-6 bg-[#FFFBEB] border border-[#FEF3C7] rounded-xl p-4 flex items-center justify-between">
                      <span className="bg-[#FEF3C7] text-[#D97706] text-xs font-bold px-3 py-1 rounded-md">{t('quick.optional')}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-gray-700">{t('quick.partial_breakdown')}</span>
                        <input
                          type="checkbox"
                          data-testid="quick-tx-partial"
                          className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500 cursor-pointer"
                          checked={v.partialBreakdown}
                          onChange={(e) => props.onChange({ partialBreakdown: e.target.checked })}
                        />
                      </div>
                    </div>
                    {v.partialBreakdown && (
                      <div className="flex gap-4 mt-2">
                        <div className="flex-1 text-right">
                          <label className="block text-[11px] font-bold text-gray-400 mb-1">{t('quick.cash_received')}</label>
                          <NumericInput
                            value={v.cashReceived ?? ''}
                            onChange={(val) => props.onChange({ cashReceived: val })}
                            className="w-full border border-gray-200 rounded-lg p-2 text-right text-sm font-bold font-mono focus:outline-none focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                        <div className="flex-1 text-right">
                          <label className="block text-[11px] font-bold text-gray-400 mb-1">{t('quick.remaining_on_account')}</label>
                          <NumericInput
                            value={v.remainingOnAccount ?? ''}
                            onChange={(val) => props.onChange({ remainingOnAccount: val })}
                            className="w-full border border-gray-200 rounded-lg p-2 text-right text-sm font-bold font-mono focus:outline-none focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    )}

                    {insufficientAlert}
                  </>
                )}
              </div>

              {/* LEFT COLUMN: Commission (Second child in RTL) */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 h-full flex flex-col relative">
                {!isCustomer && (
                  <>
                    <div className="flex justify-end items-center gap-2 mb-5 text-[#1E3A8A] font-extrabold text-sm">
                      <span>{t('quick.commission_details')}</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </div>

                    <div className="flex gap-3 mb-4">
                      <div className="flex-1 text-right">
                        <label className="block text-xs text-gray-400 font-bold mb-1.5">{t('comm')}</label>
                        <input
                          type="text"
                          readOnly
                          data-testid="quick-tx-cash-commission"
                          className="w-full h-[48px] bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-right font-bold text-gray-500 focus:outline-none font-mono"
                          placeholder="0"
                          value={props.summary.cashCommissionLabel}
                        />
                      </div>
                      <div className="flex-1 text-right">
                        <label className="block text-xs text-gray-400 font-bold mb-1.5">{t('quick.digital_commission')}</label>
                        <NumericInput
                          value={v.digitalCommission ?? ''}
                          onChange={(val) => props.onChange({ digitalCommission: val })}
                          className="w-full h-[48px] bg-white border border-gray-200 rounded-xl px-4 py-3 text-right font-bold text-gray-800 focus:outline-none focus:border-blue-500 font-mono"
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="flex items-start justify-start gap-2 text-[10px] text-blue-600 mb-4 bg-blue-50 p-2.5 rounded-lg border border-blue-100/50 text-right">
                      <Info className="w-4 h-4 shrink-0 text-blue-500" />
                      <span className="leading-relaxed font-bold">
                        {t('quick.commission_distribution_info')}
                      </span>
                    </div>

                    <button
                      type="button"
                      data-testid="quick-tx-auto-commission"
                      className="w-full bg-[#2563EB] hover:bg-blue-700 text-white py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 mb-4 transition-colors shadow-sm"
                      onClick={props.onAutoCommission}
                    >
                      {t('quick.calc_digital_commission', { rate: props.summary.digitalCommissionRateLabel })}
                      <Calculator className="w-4 h-4" />
                    </button>
                  </>
                )}

                <div className="flex-1">
                  <label className="block text-xs text-gray-400 font-bold mb-1.5 text-right">{t('note')}</label>
                  <textarea
                    data-testid="quick-tx-note"
                    className="w-full border border-gray-200 rounded-xl p-3 text-right text-sm resize-none h-20 focus:outline-none focus:border-blue-500"
                    placeholder={t('note')}
                    value={v.note || ''}
                    onChange={(e) => props.onChange({ note: e.target.value })}
                  ></textarea>
                </div>

                {isCustomer && (
                  <div className="mt-4 bg-[#F5F3FF] border border-[#EDE9FE] rounded-2xl p-3.5 space-y-3">
                    {/* Header & Status */}
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1.5">
                        <Bell className="w-4 h-4 text-purple-600 animate-pulse" />
                        <span className="text-xs font-bold text-purple-900 text-right">
                          {t('quick.due_alert_title')}
                        </span>
                      </div>
                      {v.dueDate && (
                        <button
                          type="button"
                          onClick={() => props.onChange({ dueDate: "", dueHour: "12", dueMinute: "00", dueSecond: "00" })}
                          className="text-[10px] font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 px-2 py-0.5 rounded-lg border border-rose-200 transition-colors cursor-pointer"
                        >
                          {t('quick.due_clear')}
                        </button>
                      )}
                    </div>

                    {/* Scheduled Time Preview Badge */}
                    {v.dueDate && (
                      <div className="bg-purple-600 text-white px-3 py-1.5 rounded-xl flex items-center justify-between text-xs font-bold shadow-xs">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-purple-200" />
                          <span className="text-[11px]">{t('quick.due_scheduled')}</span>
                        </div>
                        <span className="font-mono text-xs font-black tracking-wider direction-ltr">
                          {props.summary.dueAtLabel ?? `${v.dueDate} ${pad2(v.dueHour)}:${pad2(v.dueMinute)}:${pad2(v.dueSecond)}`}
                        </span>
                      </div>
                    )}

                    {/* Date Picker */}
                    <div className="space-y-1.5">
                      <label className="block text-[11px] font-bold text-purple-900 text-right">
                        {t('quick.due_date')}
                      </label>
                      <div className="flex items-center gap-2 bg-white border border-[#EDE9FE] rounded-lg p-1.5">
                        <Calendar className="w-4 h-4 text-purple-500 shrink-0" />
                        <EnglishDatePicker
                          value={v.dueDate || ''}
                          onChange={(val) => props.onChange({ dueDate: val })}
                          className="w-full text-right text-xs bg-transparent focus:outline-none"
                        />
                      </div>

                      {/* Quick Date Shortcuts */}
                      <div className="flex flex-wrap items-center gap-1 pt-0.5">
                        <span className="text-[10px] font-bold text-purple-700 shrink-0">
                          {t('quick.due_quick')}
                        </span>
                        <button type="button" onClick={() => props.onDueDateShortcut?.(0)} className={DUE_SHORTCUT_BTN}>
                          {t('quick.due_today')}
                        </button>
                        <button type="button" onClick={() => props.onDueDateShortcut?.(1)} className={DUE_SHORTCUT_BTN}>
                          +1 {t('quick.due_tomorrow')}
                        </button>
                        <button type="button" onClick={() => props.onDueDateShortcut?.(3)} className={DUE_SHORTCUT_BTN}>
                          +3 {t('quick.due_days')}
                        </button>
                        <button type="button" onClick={() => props.onDueDateShortcut?.(7)} className={DUE_SHORTCUT_BTN}>
                          +7 {t('quick.due_days')}
                        </button>
                        <button type="button" onClick={() => props.onDueDateShortcut?.(30)} className={DUE_SHORTCUT_BTN}>
                          +30 {t('quick.due_days_30')}
                        </button>
                      </div>
                    </div>

                    {/* Precise Time Picker: Hour, Minute, Second */}
                    <div className="space-y-1.5 pt-2 border-t border-[#E9D5FF]/60">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-purple-900 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-purple-600" />
                          <span>{t('quick.due_time_label')}</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => props.onDueTimeNow?.()}
                          className="text-[10px] font-bold text-purple-700 bg-purple-100 hover:bg-purple-200 px-2 py-0.5 rounded-md transition-colors cursor-pointer"
                        >
                          ⚡ {t('quick.due_time_now')}
                        </button>
                      </div>

                      {/* Digital HH : MM : SS controls */}
                      <div className="bg-white border border-[#EDE9FE] rounded-xl p-2 flex items-center justify-center gap-2" dir="ltr">
                        {/* Hour Box */}
                        <TimeBox label={t('quick.due_hour')} value={v.dueHour} max={23} placeholder="12" testId="quick-tx-due-hour" onValue={(val) => setTime({ dueHour: val })} />

                        <span className="font-mono font-black text-purple-600 text-sm pb-1">:</span>

                        {/* Minute Box */}
                        <TimeBox label={t('quick.due_minute')} value={v.dueMinute} max={59} placeholder="00" testId="quick-tx-due-minute" onValue={(val) => setTime({ dueMinute: val })} />

                        <span className="font-mono font-black text-purple-600 text-sm pb-1">:</span>

                        {/* Second Box */}
                        <TimeBox label={t('quick.due_second')} value={v.dueSecond} max={59} placeholder="00" testId="quick-tx-due-second" onValue={(val) => setTime({ dueSecond: val })} />
                      </div>

                      {/* Quick Time Presets */}
                      <div className="flex flex-wrap items-center gap-1 pt-0.5">
                        <span className="text-[10px] font-bold text-purple-700 shrink-0">
                          {t('quick.due_presets')}
                        </span>
                        <button type="button" onClick={() => setTime({ dueHour: "09", dueMinute: "00", dueSecond: "00" })} className={DUE_SHORTCUT_BTN}>
                          09:00:00 {t('quick.due_am')}
                        </button>
                        <button type="button" onClick={() => setTime({ dueHour: "12", dueMinute: "00", dueSecond: "00" })} className={DUE_SHORTCUT_BTN}>
                          12:00:00 {t('quick.due_noon')}
                        </button>
                        <button type="button" onClick={() => setTime({ dueHour: "18", dueMinute: "00", dueSecond: "00" })} className={DUE_SHORTCUT_BTN}>
                          18:00:00 {t('quick.due_pm')}
                        </button>
                        <button type="button" onClick={() => setTime({ dueHour: "23", dueMinute: "59", dueSecond: "59" })} className={DUE_SHORTCUT_BTN}>
                          23:59:59 {t('quick.due_end_of_day')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Added: server / validation error line (the old modal used toasts); rendered only when present. */}
          {props.error && (
            <p data-testid="quick-tx-error" className="px-6 pb-2 text-xs font-bold text-rose-500 text-center shrink-0">{props.error}</p>
          )}

          {/* Modal Footer */}
          <div className="px-6 py-4 flex items-center shrink-0 border-t border-gray-100">
              <button
                type="submit"
                disabled={props.busy}
                data-testid="quick-tx-submit"
                className={`w-full text-white font-bold py-3.5 rounded-xl transition-colors text-sm ${type === 'deposit' ? 'bg-[#3B82F6] hover:bg-blue-600' : 'bg-[#10B981] hover:bg-emerald-600'}`}
              >
                {t('confirm')}
              </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
